import {
  PPQ,
  ticks as durTicks,
  midiOf,
  spellMidi,
  barCapacity,
  type Duration,
  type DurBase,
  type Pitch,
  type Step,
  type Alter,
} from '@simplenotation/theory';
import type {
  Score,
  Track,
  Beat,
  Note,
  Clef,
  BarInfo,
  Direction,
  TimeSignature,
  KeySignature,
  TempoMark,
  Dynamic,
  Articulation,
  BendPoint,
  Mode,
} from '../../model/types';
import { DYNAMICS, SCORE_VERSION } from '../../model/types';
import { createTrack, newId, barRests, createRest } from '../../model/factories';
import { instrumentDef, type InstrumentDef } from '../../model/instruments';
import { validateScore, LIMITS } from '../../model/validate';
import { parseXml, decodeXmlBytes, elements, child, childText, childNum, attrNum, textOf, type XmlElement } from '../xml';
import { isZip, listZip, readZipEntry, type ZipEntry } from '../zip';
import { soundForXml, soundForProgram, instrumentByName, instrumentByKeywords, instrumentForProgram, fretInstrumentForTuning } from '../sounds';
import { concertKey, baseOfType, durationsForTicks, KIND_SUFFIX, stepAlterText } from '../common';
import { fingerTrack } from '../fingering';

export interface ImportResult {
  score: Score;
  warnings: string[];
}

/** Parse MusicXML (.musicxml/.xml text) or compressed MusicXML (.mxl bytes) into a Score. */
export async function importMusicXml(data: string | Uint8Array): Promise<Score> {
  return (await importMusicXmlWithWarnings(data)).score;
}

/** Like importMusicXml, also returning the non-fatal problems found. */
export async function importMusicXmlWithWarnings(data: string | Uint8Array): Promise<ImportResult> {
  const text = typeof data === 'string' ? data : await musicXmlText(data);
  return convertDocument(parseXml(text));
}

function findAll(el: XmlElement, name: string, out: XmlElement[] = []): XmlElement[] {
  for (const c of el.children) {
    if (typeof c === 'string') continue;
    if (c.name === name) out.push(c);
    findAll(c, name, out);
  }
  return out;
}

async function musicXmlText(bytes: Uint8Array): Promise<string> {
  if (!isZip(bytes)) return decodeXmlBytes(bytes);
  const entries = listZip(bytes);
  const byName = (n: string): ZipEntry | undefined => entries.find((e) => e.name === n) ?? entries.find((e) => e.name.toLowerCase() === n.toLowerCase());
  let target: ZipEntry | undefined;
  const container = byName('META-INF/container.xml');
  if (container) {
    const c = parseXml(decodeXmlBytes(await readZipEntry(bytes, container)));
    const rootfiles = findAll(c, 'rootfile');
    const pick = rootfiles.find((r) => !r.attrs['media-type'] || /musicxml/i.test(r.attrs['media-type'])) ?? rootfiles[0];
    const fp = pick?.attrs['full-path'];
    if (fp) target = byName(fp);
  }
  if (!target) target = entries.find((e) => !/^meta-inf\//i.test(e.name) && /\.(musicxml|xml)$/i.test(e.name));
  if (!target) throw new Error('No MusicXML score found in the compressed (.mxl) archive');
  return decodeXmlBytes(await readZipEntry(bytes, target));
}

function timewiseToPartwise(root: XmlElement): XmlElement {
  const parts = new Map<string, XmlElement>();
  const order: string[] = [];
  for (const m of elements(root, 'measure')) {
    for (const p of elements(m, 'part')) {
      const id = p.attrs.id ?? '';
      let part = parts.get(id);
      if (!part) {
        part = { name: 'part', attrs: { id }, children: [] };
        parts.set(id, part);
        order.push(id);
      }
      part.children.push({ name: 'measure', attrs: { ...m.attrs }, children: p.children });
    }
  }
  return {
    name: 'score-partwise',
    attrs: root.attrs,
    children: [...root.children.filter((c) => typeof c !== 'string' && c.name !== 'measure'), ...order.map((id) => parts.get(id)!)],
  };
}

// ---------------------------------------------------------------- raw parse

interface RawNote {
  el: XmlElement;
  staff: number;
  voice: string;
  onset: number;
  dur: number;
  chord: boolean;
  grace?: 'acciaccatura' | 'appoggiatura';
  rest: boolean;
  measureRest: boolean;
  /** MusicXML pitch (or display position for unpitched notes). */
  pitch?: Pitch;
  unpitched: boolean;
  string?: number;
  fret?: number;
}

interface RawEvent {
  pos: number;
  el: XmlElement;
  staff?: number;
  voice?: string;
  /** Staff/voice of the next note in the stream (fallback association). */
  nextStaff?: number;
  nextVoice?: string;
}

interface RawMeasure {
  length: number;
  implicit: boolean;
  notes: RawNote[];
  events: RawEvent[];
  attrs: { pos: number; el: XmlElement }[];
  barlines: XmlElement[];
  newSystem: boolean;
}

const TOL = 4;

function parsePitch(el: XmlElement | undefined, warn: (s: string) => void): Pitch | undefined {
  if (!el) return undefined;
  const step = (childText(el, 'step') ?? childText(el, 'display-step') ?? '').toUpperCase();
  const octave = childNum(el, 'octave') ?? childNum(el, 'display-octave');
  if (!'CDEFGAB'.includes(step) || step.length !== 1 || octave === undefined) return undefined;
  let alter = Math.round(childNum(el, 'alter') ?? 0);
  if (Math.abs(alter) > 2) {
    warn(`Pitch with alteration ${alter} respelled`);
    const p = spellMidi(midiOf({ step: step as Step, alter: 0, octave: Math.round(octave) }) + alter, 0);
    return p;
  }
  if (Object.is(alter, -0)) alter = 0;
  return { step: step as Step, alter: alter as Alter, octave: Math.round(octave) };
}

function parsePart(part: XmlElement, warn: (s: string) => void): RawMeasure[] {
  const out: RawMeasure[] = [];
  let divisions = 1;
  for (const m of elements(part, 'measure')) {
    const meas: RawMeasure = { length: 0, implicit: m.attrs.implicit === 'yes', notes: [], events: [], attrs: [], barlines: [], newSystem: false };
    let cursor = 0;
    let maxPos = 0;
    let lastOnset = 0;
    let pendingEvents: RawEvent[] = [];
    const scale = () => PPQ / (divisions > 0 ? divisions : 1);
    for (const c of m.children) {
      if (typeof c === 'string') continue;
      switch (c.name) {
        case 'attributes': {
          const d = childNum(c, 'divisions');
          if (d && d > 0) divisions = d;
          meas.attrs.push({ pos: cursor, el: c });
          break;
        }
        case 'note': {
          const isChord = !!child(c, 'chord');
          const graceEl = child(c, 'grace');
          const dur = (childNum(c, 'duration') ?? 0) * scale();
          const onset = isChord ? lastOnset : cursor;
          const restEl = child(c, 'rest');
          const unp = child(c, 'unpitched');
          const tech = elements(c, 'notations').flatMap((n) => elements(n, 'technical'));
          const sNum = tech.map((t) => childNum(t, 'string')).find((x) => x !== undefined);
          const fNum = tech.map((t) => childNum(t, 'fret')).find((x) => x !== undefined);
          const staff = childNum(c, 'staff') ?? 1;
          const voice = childText(c, 'voice') || '1';
          const rn: RawNote = {
            el: c,
            staff,
            voice,
            onset,
            dur,
            chord: isChord,
            grace: graceEl ? (graceEl.attrs.slash === 'yes' ? 'acciaccatura' : 'appoggiatura') : undefined,
            rest: !!restEl,
            measureRest: restEl?.attrs.measure === 'yes',
            pitch: restEl ? undefined : parsePitch(child(c, 'pitch') ?? unp, warn),
            unpitched: !!unp,
            string: sNum,
            fret: fNum,
          };
          meas.notes.push(rn);
          for (const e of pendingEvents) {
            e.nextStaff = staff;
            e.nextVoice = voice;
          }
          pendingEvents = [];
          if (!isChord) lastOnset = cursor;
          if (!isChord && !graceEl) cursor += dur;
          maxPos = Math.max(maxPos, cursor);
          break;
        }
        case 'backup':
          cursor = Math.max(0, cursor - (childNum(c, 'duration') ?? 0) * scale());
          break;
        case 'forward':
          cursor += (childNum(c, 'duration') ?? 0) * scale();
          maxPos = Math.max(maxPos, cursor);
          break;
        case 'direction':
        case 'harmony':
        case 'sound': {
          const off = (childNum(c, 'offset') ?? 0) * scale();
          const ev: RawEvent = { pos: Math.max(0, cursor + off), el: c, staff: childNum(c, 'staff'), voice: childText(c, 'voice') || undefined };
          meas.events.push(ev);
          pendingEvents.push(ev);
          break;
        }
        case 'barline':
          meas.barlines.push(c);
          break;
        case 'print':
          if (c.attrs['new-system'] === 'yes' || c.attrs['new-page'] === 'yes') meas.newSystem = true;
          break;
        default:
          break;
      }
    }
    meas.length = maxPos;
    out.push(meas);
  }
  return out;
}

// ---------------------------------------------------------------- part setup

interface PartInfo {
  id: string;
  name: string;
  abbr?: string;
  instrumentName?: string;
  instrumentSound?: string;
  /** 0-based GM program. */
  program?: number;
  channel?: number;
  volume?: number;
  pan?: number;
}

interface StaffSetup {
  clefEl?: XmlElement;
  details?: XmlElement;
  isTab: boolean;
  isPerc: boolean;
}

interface TrackPlan {
  part: number;
  /** MusicXML staff number holding the notation. */
  staff: number;
  /** TAB staff whose string/fret data is merged in. */
  tabStaff?: number;
  tabOnly: boolean;
  clef: Clef;
  /** MusicXML pitch = written pitch + xmlShift (semitones, multiple of 12). */
  xmlShift: number;
  w2s: number;
  def: InstrumentDef;
  tuning?: number[];
  capo?: number;
  sound: string;
  name: string;
  shortName?: string;
  volume?: number;
  pan?: number;
  voiceMap: Map<string, number>;
}

function clefFromXml(el: XmlElement | undefined): { clef: Clef; shift: number; tab: boolean; perc: boolean } {
  if (!el) return { clef: 'treble', shift: 0, tab: false, perc: false };
  const sign = (childText(el, 'sign') ?? 'G').toUpperCase();
  const line = childNum(el, 'line');
  const oc = Math.round(childNum(el, 'clef-octave-change') ?? 0);
  const shift = 12 * oc;
  if (sign === 'TAB') return { clef: 'treble', shift: 0, tab: true, perc: false };
  if (sign === 'PERCUSSION' || sign === 'NONE') return { clef: 'percussion', shift: 0, tab: false, perc: sign === 'PERCUSSION' };
  if (sign === 'F') return { clef: 'bass', shift, tab: false, perc: false };
  if (sign === 'C') return { clef: line !== undefined && line >= 4 ? 'tenor' : 'alto', shift, tab: false, perc: false };
  // G (and anything else)
  return { clef: oc === -1 ? 'treble8vb' : 'treble', shift, tab: false, perc: false };
}

function clefOfChange(el: XmlElement): Clef | null {
  const c = clefFromXml(el);
  if (c.tab) return null;
  return c.clef;
}

function tuningFromDetails(d: XmlElement | undefined): number[] | undefined {
  if (!d) return undefined;
  const st = elements(d, 'staff-tuning');
  if (!st.length) return undefined;
  const lines = st
    .map((t, i) => {
      const step = (childText(t, 'tuning-step') ?? 'E').toUpperCase() as Step;
      const alter = Math.round(childNum(t, 'tuning-alter') ?? 0);
      const octave = childNum(t, 'tuning-octave') ?? 3;
      const midi = 'CDEFGAB'.includes(step) ? midiOf({ step, alter: 0, octave }) + alter : 52;
      return { line: attrNum(t, 'line') ?? i + 1, midi };
    })
    .sort((a, b) => a.line - b.line);
  // Line 1 = lowest string; the model lists string 1 (highest) first.
  return lines.map((l) => Math.max(0, Math.min(127, l.midi))).reverse();
}

// ---------------------------------------------------------------- main conversion

function convertDocument(rootIn: XmlElement): ImportResult {
  const warnings: string[] = [];
  const warned = new Set<string>();
  const warn = (s: string) => {
    if (warned.has(s)) return;
    warned.add(s);
    warnings.push(s);
  };
  let root = rootIn;
  if (root.name === 'score-timewise') root = timewiseToPartwise(root);
  if (root.name !== 'score-partwise') throw new Error(`Not a MusicXML score (root element <${root.name}>)`);

  // ---- metadata
  const workTitle = childText(child(root, 'work'), 'work-title');
  const movementTitle = childText(root, 'movement-title');
  const credits = elements(root, 'credit');
  const creditOf = (type: string) => {
    for (const c of credits) {
      if (elements(c, 'credit-type').some((t) => textOf(t).trim() === type)) {
        const w = elements(c, 'credit-words').map((x) => textOf(x).trim()).filter(Boolean);
        if (w.length) return w.join(' ');
      }
    }
    return undefined;
  };
  const ident = child(root, 'identification');
  const creator = (type: string) =>
    elements(ident, 'creator')
      .filter((c) => (c.attrs.type ?? '').toLowerCase() === type)
      .map((c) => textOf(c).trim())
      .filter(Boolean)
      .join(', ') || undefined;
  const title = workTitle || movementTitle || creditOf('title') || 'Imported score';
  let subtitle = creditOf('subtitle');
  if (!subtitle && workTitle && movementTitle && workTitle !== movementTitle) subtitle = movementTitle;
  const meta: Score['meta'] = { title };
  if (subtitle) meta.subtitle = subtitle;
  const composer = creator('composer') ?? creditOf('composer');
  if (composer) meta.composer = composer;
  const artist = creator('artist') ?? creator('performer');
  if (artist) meta.artist = artist;
  const arranger = creator('arranger') ?? creditOf('arranger');
  if (arranger) meta.arranger = arranger;
  const rights = elements(ident, 'rights')
    .map((r) => textOf(r).trim())
    .filter(Boolean)
    .join('; ');
  if (rights) meta.copyright = rights;
  else {
    const cr = creditOf('rights');
    if (cr) meta.copyright = cr;
  }

  // ---- part list
  const partInfos = new Map<string, PartInfo>();
  const partList = child(root, 'part-list');
  for (const sp of findAll(partList ?? { name: '', attrs: {}, children: [] }, 'score-part')) {
    const id = sp.attrs.id ?? '';
    const si = child(sp, 'score-instrument');
    const mi = child(sp, 'midi-instrument');
    const prog = childNum(mi, 'midi-program');
    const info: PartInfo = {
      id,
      name: (childText(sp, 'part-name') ?? '').trim(),
      abbr: childText(sp, 'part-abbreviation') || undefined,
      instrumentName: childText(si, 'instrument-name') || undefined,
      instrumentSound: childText(si, 'instrument-sound') || undefined,
      program: prog !== undefined ? Math.max(0, Math.min(127, Math.round(prog) - 1)) : undefined,
      channel: childNum(mi, 'midi-channel'),
      volume: childNum(mi, 'volume'),
      pan: childNum(mi, 'pan'),
    };
    partInfos.set(id, info);
  }

  const partEls = elements(root, 'part');
  if (!partEls.length) throw new Error('MusicXML score has no parts');
  const parts = partEls.map((p) => parsePart(p, warn));
  let nBars = Math.max(1, ...parts.map((p) => p.length));
  if (nBars > LIMITS.bars) {
    warn(`Score has ${nBars} measures; only the first ${LIMITS.bars} were imported`);
    nBars = LIMITS.bars;
  }

  // ---- track plans
  const plans: TrackPlan[] = [];
  partEls.forEach((pel, pi) => {
    const raw = parts[pi];
    const info = partInfos.get(pel.attrs.id ?? '') ?? { id: pel.attrs.id ?? '', name: '' };
    const staves = new Map<number, StaffSetup>();
    const getStaff = (n: number) => {
      let s = staves.get(n);
      if (!s) {
        s = { isTab: false, isPerc: false };
        staves.set(n, s);
      }
      return s;
    };
    let staffCount = 1;
    let transposeTotal: Record<number, number> = {};
    let haveTranspose = false;
    for (const m of raw) {
      for (const a of m.attrs) {
        const sc = childNum(a.el, 'staves');
        if (sc) staffCount = Math.max(staffCount, sc);
        for (const c of elements(a.el, 'clef')) {
          const n = attrNum(c, 'number') ?? 1;
          const s = getStaff(n);
          if (!s.clefEl) {
            s.clefEl = c;
            const cf = clefFromXml(c);
            s.isTab = cf.tab;
            s.isPerc = cf.perc;
          }
        }
        for (const d of elements(a.el, 'staff-details')) {
          const n = attrNum(d, 'number') ?? 1;
          const s = getStaff(n);
          if (!s.details) s.details = d;
        }
        if (!haveTranspose) {
          const trs = elements(a.el, 'transpose');
          if (trs.length) {
            haveTranspose = true;
            transposeTotal = {};
            for (const t of trs) {
              const total = Math.round((childNum(t, 'chromatic') ?? 0) + 12 * (childNum(t, 'octave-change') ?? 0) + (child(t, 'double') ? -12 : 0));
              const n = attrNum(t, 'number');
              if (n === undefined) transposeTotal[0] = total;
              else transposeTotal[n] = total;
            }
          }
        }
      }
      for (const n of m.notes) staffCount = Math.max(staffCount, n.staff);
    }
    for (let s = 1; s <= staffCount; s++) getStaff(s);
    const staffNums = [...staves.keys()].filter((n) => n <= staffCount).sort((a, b) => a - b);
    const standard = staffNums.filter((n) => !staves.get(n)!.isTab);
    const tabs = staffNums.filter((n) => staves.get(n)!.isTab);
    const anyTuning = staffNums.map((n) => tuningFromDetails(staves.get(n)!.details)).find((t) => t && t.length);
    const capoOf = (n?: number) => (n === undefined ? undefined : childNum(staves.get(n)?.details, 'capo'));
    const anyCapo = staffNums.map((n) => capoOf(n)).find((c) => c !== undefined);

    const mk = (staff: number, tabStaff: number | undefined, tabOnly: boolean, idx: number, total: number) => {
      const setup = staves.get(staff)!;
      const cf = clefFromXml(setup.clefEl);
      const tuning = tuningFromDetails(staves.get(tabStaff ?? staff)?.details) ?? anyTuning;
      // Instrument choice.
      let def = instrumentByName(info.instrumentName);
      if (!def && (setup.isPerc || info.channel === 10)) def = instrumentDef('drums');
      if (!def) def = instrumentByKeywords(info.name) ?? instrumentByKeywords(info.instrumentName);
      if (!def && info.instrumentSound) {
        const snd = soundForXml(info.instrumentSound, info.program);
        if (snd) {
          if (info.program !== undefined) {
            const byProg = instrumentForProgram(info.program);
            if (byProg.sound === snd) def = byProg;
          }
          def ??= instrumentDefsBySound(snd);
        }
      }
      if (!def && info.program !== undefined) def = instrumentForProgram(info.program);
      if (!def && (tuning || tabOnly || tabStaff !== undefined)) def = fretInstrumentForTuning(tuning ?? [64, 59, 55, 50, 45, 40]);
      def ??= instrumentDef('piano');
      if ((tuning || tabOnly || tabStaff !== undefined) && !def.tuning) def = fretInstrumentForTuning(tuning ?? [64, 59, 55, 50, 45, 40]);
      const fretted = !!def.tuning;
      const trackTuning = fretted ? (tuning && tuning.length ? tuning.slice(0, 12) : [...def.tuning!]) : undefined;
      const transpose = transposeTotal[staff] ?? transposeTotal[0] ?? 0;
      let clef: Clef;
      let xmlShift: number;
      let w2s: number;
      if (tabOnly) {
        clef = def.clef;
        xmlShift = def.writtenToSounding;
        w2s = def.writtenToSounding;
      } else if (setup.isPerc || def.id === 'drums') {
        clef = 'percussion';
        xmlShift = 0;
        w2s = 0;
      } else {
        clef = setup.clefEl ? cf.clef : def.clef === 'treble8vb' ? 'treble' : def.clef;
        xmlShift = cf.shift;
        w2s = transpose + cf.shift;
      }
      let sound = def.sound;
      if (def.id !== 'drums') {
        const snd = soundForXml(info.instrumentSound, info.program) ?? (info.program !== undefined ? soundForProgram(info.program) : undefined);
        if (snd && (instrumentByName(info.instrumentName) || sameFamily(snd, def))) sound = snd;
      }
      const baseName = info.name || info.instrumentName || def.name.replace(/ \(.*\)$/, '');
      const plan: TrackPlan = {
        part: pi,
        staff,
        tabStaff,
        tabOnly,
        clef,
        xmlShift,
        w2s,
        def,
        tuning: trackTuning,
        capo: fretted ? Math.max(0, Math.round(capoOf(tabStaff ?? staff) ?? anyCapo ?? 0)) : undefined,
        sound,
        name: total > 1 ? `${baseName} (${idx + 1})` : baseName,
        shortName: info.abbr,
        volume: info.volume !== undefined ? Math.max(0, Math.min(1, info.volume / 100)) : undefined,
        pan: info.pan !== undefined ? Math.max(-1, Math.min(1, info.pan / 90)) : undefined,
        voiceMap: new Map(),
      };
      // Voice mapping (MusicXML voice ids → 0..3).
      const ids: string[] = [];
      for (const m of raw) for (const n of m.notes) if (n.staff === staff && !ids.includes(n.voice)) ids.push(n.voice);
      ids.sort((a, b) => {
        const na = Number(a);
        const nb = Number(b);
        if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
        return 0;
      });
      ids.forEach((id, i) => {
        if (i < LIMITS.voices) plan.voiceMap.set(id, i);
        else warn(`Part "${baseName}": more than ${LIMITS.voices} voices; extra voices were dropped`);
      });
      return plan;
    };
    if (standard.length) {
      standard.forEach((s, i) => plans.push(mk(s, i === 0 ? tabs[0] : undefined, false, i, standard.length)));
    } else {
      plans.push(mk(tabs[0] ?? 1, tabs[0], true, 0, 1));
    }
  });
  if (plans.length > LIMITS.tracks) {
    warn(`Score has ${plans.length} staves; only the first ${LIMITS.tracks} were imported`);
    plans.length = LIMITS.tracks;
  }

  // ---- bar infos
  const bars: BarInfo[] = Array.from({ length: nBars }, () => ({}));
  const tempos: TempoMark[] = [];
  const tempoKeys = new Set<string>();
  const firstPlanOfPart = (pi: number) => plans.find((p) => p.part === pi);
  for (let m = 0; m < nBars; m++) {
    for (let pi = 0; pi < parts.length; pi++) {
      const rm = parts[pi][m];
      if (!rm) continue;
      const plan = firstPlanOfPart(pi);
      for (const a of rm.attrs) {
        const t = child(a.el, 'time');
        if (t && !bars[m].timeSig && !child(t, 'senza-misura')) {
          const num = Math.round(Number((childText(t, 'beats') ?? '4').split('+').reduce((x, y) => x + Number(y), 0)));
          const den = Math.round(childNum(t, 'beat-type') ?? 4);
          const sym = t.attrs.symbol;
          if ([1, 2, 4, 8, 16, 32, 64].includes(den) && num >= 1 && num <= 64) {
            const ts: TimeSignature = { num, den: den as DurBase };
            if (sym === 'common' || sym === 'cut') ts.symbol = sym;
            bars[m].timeSig = ts;
          } else warn(`Unsupported time signature ${num}/${den} ignored`);
        }
        const k = child(a.el, 'key');
        if (k && !bars[m].keySig && plan && plan.clef !== 'percussion' && !plan.tabOnly) {
          const f = childNum(k, 'fifths');
          if (f === undefined) warn('Non-traditional key signature treated as C major');
          const modeText = (childText(k, 'mode') ?? 'major').toLowerCase();
          const mode: Mode = (['major', 'minor', 'dorian', 'phrygian', 'lydian', 'mixolydian', 'locrian'] as const).includes(modeText as Mode)
            ? (modeText as Mode)
            : modeText === 'aeolian'
              ? 'minor'
              : 'major';
          const written: KeySignature = { fifths: Math.max(-7, Math.min(7, Math.round(f ?? 0))), mode };
          bars[m].keySig = concertKey(written, plan.w2s);
        }
      }
    }
  }
  if (!bars[0].timeSig) bars[0].timeSig = { num: 4, den: 4 };
  if (!bars[0].keySig) bars[0].keySig = { fifths: 0, mode: 'major' };
  // Drop redundant repeats of the same time/key signature.
  {
    let ts = bars[0].timeSig!;
    let ks = bars[0].keySig!;
    for (let m = 1; m < nBars; m++) {
      const t = bars[m].timeSig;
      if (t) {
        if (t.num === ts.num && t.den === ts.den && t.symbol === ts.symbol) delete bars[m].timeSig;
        else ts = t;
      }
      const k = bars[m].keySig;
      if (k) {
        if (k.fifths === ks.fifths && k.mode === ks.mode) delete bars[m].keySig;
        else ks = k;
      }
    }
  }
  const tsAt: TimeSignature[] = [];
  {
    let ts = bars[0].timeSig!;
    for (let m = 0; m < nBars; m++) {
      if (bars[m].timeSig) ts = bars[m].timeSig!;
      tsAt.push(ts);
    }
  }
  const capAt = (m: number) => barCapacity(tsAt[Math.min(m, nBars - 1)]);

  // Structure (barlines/repeats/endings) from the part with the most barline information.
  let structPart = 0;
  {
    let best = -1;
    parts.forEach((p, pi) => {
      const c = p.reduce((a, m) => a + m.barlines.length, 0);
      if (c > best) {
        best = c;
        structPart = pi;
      }
    });
  }
  let openEnding: number[] | null = null;
  for (let m = 0; m < nBars; m++) {
    const rm = parts[structPart][m];
    if (!rm) {
      if (openEnding) bars[m].endings = [...openEnding];
      continue;
    }
    const left = rm.barlines.filter((b) => (b.attrs.location ?? 'right') === 'left');
    const right = rm.barlines.filter((b) => (b.attrs.location ?? 'right') === 'right');
    for (const b of left) {
      const rep = child(b, 'repeat');
      if (rep && (rep.attrs.direction ?? 'forward') === 'forward') bars[m].repeatStart = true;
      const e = child(b, 'ending');
      if (e && (e.attrs.type ?? 'start') === 'start') openEnding = parseEndingNumbers(e.attrs.number ?? '1');
    }
    // Endings may also start in a right/middle barline element in some files.
    for (const b of right) {
      const e = child(b, 'ending');
      if (e && e.attrs.type === 'start') openEnding = parseEndingNumbers(e.attrs.number ?? '1');
    }
    if (openEnding) bars[m].endings = [...openEnding];
    for (const b of right) {
      const rep = child(b, 'repeat');
      const style = childText(b, 'bar-style');
      if (rep && rep.attrs.direction === 'backward') {
        const times = attrNum(rep, 'times');
        bars[m].repeatEnd = Math.max(2, Math.min(99, Math.round(times ?? 2)));
      } else if (rep && rep.attrs.direction === 'forward' && m + 1 < nBars) bars[m + 1].repeatStart = true;
      if (!rep) {
        if (style === 'light-light' || style === 'heavy-heavy') bars[m].barline = 'double';
        else if (style === 'dashed' || style === 'dotted') bars[m].barline = 'dashed';
        else if (style === 'light-heavy' || style === 'heavy') bars[m].barline = 'final';
      } else if (style === 'light-heavy' && m === nBars - 1 && !bars[m].repeatEnd) bars[m].barline = 'final';
      const e = child(b, 'ending');
      if (e && (e.attrs.type === 'stop' || e.attrs.type === 'discontinue')) openEnding = null;
    }
    if (rm.newSystem && m > 0) bars[m - 1].lineBreak = 'force';
  }
  // Pickup.
  {
    const lens = parts.map((p) => p[0]?.length ?? 0);
    const implicit = parts.some((p) => p[0]?.implicit);
    const len = Math.max(...lens);
    // An explicitly implicit first measure, or one that is shorter than its time signature.
    if (nBars > 1 && len > 0 && len < capAt(0) - TOL) bars[0].pickup = true;
    void implicit;
  }

  // Score-level directions (all parts, deduplicated).
  for (let m = 0; m < nBars; m++) {
    for (let pi = 0; pi < parts.length; pi++) {
      const rm = parts[pi][m];
      if (!rm) continue;
      for (const ev of rm.events) {
        if (ev.el.name === 'harmony') continue;
        scoreLevelEvent(ev, m, bars, tempos, tempoKeys, capAt(m), warn);
      }
    }
  }
  tempos.sort((a, b) => a.bar - b.bar || a.beatOffsetTicks - b.beatOffsetTicks);
  if (!tempos.length || tempos[0].bar !== 0 || tempos[0].beatOffsetTicks !== 0) tempos.unshift({ bar: 0, beatOffsetTicks: 0, bpm: 120, beatUnit: 4 });

  // ---- tracks
  const tracks: Track[] = [];
  const ctxs: TrackCtx[] = [];
  for (const plan of plans) {
    const track = createTrack(nBars, bars[0].timeSig!, {
      instrument: plan.def.id,
      name: plan.name,
      tuning: plan.tuning,
      capo: plan.capo,
      showStaff: !plan.tabOnly,
      showTab: !!plan.tuning && (plan.tabOnly || plan.tabStaff !== undefined || !!plan.def.showTab),
    });
    track.sound = plan.sound;
    track.writtenToSounding = plan.w2s;
    track.clef = plan.clef;
    if (plan.shortName) track.shortName = plan.shortName;
    else delete track.shortName;
    if (plan.volume !== undefined) track.volume = plan.volume;
    if (plan.pan !== undefined && plan.pan !== 0) track.pan = plan.pan;
    if (!track.tuning) delete track.tuning;
    if (track.capo === undefined) delete track.capo;
    const ctx = buildTrack(track, plan, parts[plan.part], nBars, tsAt, bars, warn);
    tracks.push(track);
    ctxs.push(ctx);
  }

  const score: Score = { v: SCORE_VERSION, meta, bars, tempos, tracks };
  collapseMultiRests(score, ctxs);
  const res = validateScore(score);
  if (!res.ok) throw new Error(`Imported score is invalid: ${res.error}`);
  return { score, warnings };
}

function instrumentDefsBySound(sound: string): InstrumentDef | undefined {
  const map: Record<string, string> = {
    gtr_nylon: 'classical-guitar',
    gtr_steel: 'acoustic-guitar',
    gtr_clean: 'electric-guitar',
    gtr_jazz: 'electric-guitar',
    gtr_dist: 'electric-guitar-dist',
    bass_finger: 'bass-guitar',
    bass_pick: 'bass-guitar',
    bass_acoustic: 'acoustic-bass',
    banjo: 'banjo',
    mandolin: 'mandolin',
    ukulele: 'ukulele',
    harp: 'harp',
    harpsichord: 'harpsichord',
    piano: 'piano',
    epiano: 'electric-piano',
    organ: 'organ',
    accordion: 'accordion',
    harmonica: 'harmonica',
    bagpipe: 'bagpipes',
    violin: 'violin',
    viola: 'viola',
    cello: 'cello',
    contrabass: 'contrabass',
    flute: 'flute',
    recorder: 'recorder',
    clarinet: 'clarinet',
    oboe: 'oboe',
    bassoon: 'bassoon',
    sax_alto: 'alto-sax',
    sax_tenor: 'tenor-sax',
    sax_bari: 'baritone-sax',
    trumpet: 'trumpet',
    trombone: 'trombone',
    horn: 'french-horn',
    tuba: 'tuba',
    marimba: 'marimba',
    vibraphone: 'vibraphone',
    glockenspiel: 'glockenspiel',
    steel_drum: 'steel-drums',
    timpani: 'timpani',
    synth_lead: 'synth',
    choir: 'soprano-voice',
    drums: 'drums',
  };
  const id = map[sound];
  return id ? instrumentDef(id) : undefined;
}

function sameFamily(sound: string, def: InstrumentDef): boolean {
  const fam = (s: string) => (s.startsWith('gtr_') ? 'gtr' : s.startsWith('bass_') ? 'bass' : s);
  return fam(sound) === fam(def.sound);
}

function parseEndingNumbers(s: string): number[] {
  const out: number[] = [];
  for (const part of s.split(/[,\s]+/)) {
    const r = /^(\d+)\s*-\s*(\d+)$/.exec(part);
    if (r) {
      for (let i = Number(r[1]); i <= Number(r[2]) && out.length < 16; i++) out.push(i);
    } else if (/^\d+$/.test(part)) out.push(Number(part));
  }
  return out.length ? [...new Set(out)].filter((n) => n >= 1 && n <= 99) : [1];
}

function jumpFromWords(text: string): Direction | undefined {
  const t = text.trim().replace(/\s+/g, ' ');
  if (/^(d\.? ?c\.?|da capo)\b/i.test(t)) return /al fine/i.test(t) ? 'DC al Fine' : /al coda/i.test(t) ? 'DC al Coda' : 'DC';
  if (/^(d\.? ?s\.?|dal segno)\b/i.test(t)) return /al coda/i.test(t) ? 'DS al Coda' : 'DS';
  if (/^fine$/i.test(t)) return 'Fine';
  if (/^(to coda|al coda)\b/i.test(t) || /^to\s*𝄌$/u.test(t)) return 'ToCoda';
  return undefined;
}

function scoreLevelEvent(ev: RawEvent, m: number, bars: BarInfo[], tempos: TempoMark[], tempoKeys: Set<string>, cap: number, warn: (s: string) => void): void {
  const el = ev.el;
  const sound = el.name === 'sound' ? el : child(el, 'sound');
  const dtypes = el.name === 'direction' ? elements(el, 'direction-type') : [];
  const kids = dtypes.flatMap((d) => elements(d));
  const words = kids.filter((k) => k.name === 'words').map((k) => textOf(k).trim()).filter(Boolean);
  const setDir = (d: Direction) => {
    if (!bars[m].direction) bars[m].direction = d;
  };
  // Jumps and markers.
  if (sound) {
    const wordsText = words.join(' ');
    if (sound.attrs.dacapo === 'yes') setDir(jumpFromWords(wordsText)?.startsWith('DC') ? jumpFromWords(wordsText)! : 'DC');
    else if (sound.attrs.dalsegno) setDir(jumpFromWords(wordsText)?.startsWith('DS') ? jumpFromWords(wordsText)! : 'DS');
    else if (sound.attrs.fine) setDir('Fine');
    else if (sound.attrs.tocoda) setDir('ToCoda');
    else if (sound.attrs.segno) setDir('Segno');
    else if (sound.attrs.coda) setDir('Coda');
    const swing = child(sound, 'swing');
    if (swing) {
      if (child(swing, 'straight')) bars[m].tripletFeel = 'none';
      else bars[m].tripletFeel = (childText(swing, 'swing-type') ?? 'eighth') === '16th' ? '16th' : '8th';
    }
  }
  if (kids.some((k) => k.name === 'segno')) setDir('Segno');
  if (kids.some((k) => k.name === 'coda')) setDir('Coda');
  for (const w of words) {
    const j = jumpFromWords(w);
    if (j) setDir(j);
  }
  // Rehearsal marks.
  const reh = kids.filter((k) => k.name === 'rehearsal').map((k) => textOf(k).trim()).filter(Boolean);
  if (reh.length && !bars[m].section) {
    const r = reh[0];
    const sec: { name?: string; letter?: string } = {};
    if (r.length <= 2 && /^[A-Za-z0-9]+$/.test(r)) {
      sec.letter = r;
      if (words.length) sec.name = words.join(' ');
    } else sec.name = r;
    bars[m].section = sec;
  }
  // Tempo.
  const met = kids.find((k) => k.name === 'metronome');
  const soundTempo = attrNum(sound, 'tempo');
  if (met || soundTempo !== undefined) {
    let bpm: number | undefined;
    let beatUnit: DurBase = 4;
    let dotted = false;
    const units = met ? elements(met, 'beat-unit') : [];
    const pm = met ? childText(met, 'per-minute') : undefined;
    const pmNum = pm ? Number((/\d+(?:\.\d+)?/.exec(pm) ?? [''])[0]) : NaN;
    if (met && units.length === 1 && Number.isFinite(pmNum) && pmNum > 0) {
      const b = baseOfType(textOf(units[0]).trim());
      if (b && [1, 2, 4, 8, 16, 32, 64].includes(b)) beatUnit = b as DurBase;
      dotted = elements(met, 'beat-unit-dot').length > 0;
      bpm = pmNum;
    } else if (soundTempo !== undefined && soundTempo > 0) {
      bpm = soundTempo;
    }
    if (bpm !== undefined) {
      if (bpm < 5 || bpm > 1000) warn(`Tempo ${bpm} clamped`);
      bpm = Math.max(5, Math.min(1000, bpm));
      const off = Math.max(0, Math.min(Math.round(ev.pos), Math.max(0, cap - 1)));
      const key = `${m}:${off}`;
      if (!tempoKeys.has(key)) {
        tempoKeys.add(key);
        const tm: TempoMark = { bar: m, beatOffsetTicks: off, bpm, beatUnit };
        if (dotted) tm.dotted = true;
        const txt = words.filter((w) => !jumpFromWords(w)).join(' ');
        if (txt) tm.text = txt;
        tempos.push(tm);
      }
    }
  }
}

// ---------------------------------------------------------------- track building

interface Placed {
  onset: number;
  beat: Beat;
}

interface TrackCtx {
  /** [bar][voice] → non-grace beats with onsets. */
  placed: Placed[][][];
  multiRest: Map<number, number>;
}

interface NotationState {
  slurOpen: Map<string, Note>;
  slurPairs: Array<{ a: Note; b: Note }>;
  slideOpen: Map<string, Note>;
  slidePairs: Array<{ a: Note; b: Note }>;
  vibOpen: Map<number, Set<string>>;
  lyricVerses: string[];
}

function buildTrack(
  track: Track,
  plan: TrackPlan,
  raw: RawMeasure[],
  nBars: number,
  tsAt: TimeSignature[],
  bars: BarInfo[],
  warn: (s: string) => void,
): TrackCtx {
  const ctx: TrackCtx = { placed: [], multiRest: new Map() };
  const st: NotationState = { slurOpen: new Map(), slurPairs: [], slideOpen: new Map(), slidePairs: [], vibOpen: new Map(), lyricVerses: [] };
  const fretted = !!track.tuning;
  const voiceOf = (id: string | undefined): number => (id !== undefined && plan.voiceMap.has(id) ? plan.voiceMap.get(id)! : 0);
  const ranges: RangeEv[] = [];
  const wedges = new Map<string, { beat: Beat; type: 'cresc' | 'dim'; bar: number; voice: number }>();
  const attach: AttachEv[] = [];

  let curClef: Clef = track.clef;
  for (let m = 0; m < nBars; m++) {
    const rm = raw[m];
    const ts = tsAt[m];
    const cap = barCapacity(ts);
    const placedBar: Placed[][] = [];
    if (!rm) {
      track.bars[m] = { voices: [barRests(ts)] };
      ctx.placed.push([placedFor(track.bars[m].voices[0])]);
      continue;
    }
    // Clef changes and multi-rests from attributes.
    for (const a of rm.attrs) {
      if (m === 0 && a.pos <= TOL) {
        // Initial clef handled by the plan.
      } else {
        for (const c of elements(a.el, 'clef')) {
          if ((attrNum(c, 'number') ?? 1) !== plan.staff || plan.tabOnly) continue;
          const cl = clefOfChange(c);
          if (!cl || plan.clef === 'percussion') continue;
          const target = a.pos >= Math.max(cap, rm.length) - TOL && a.pos > 0 ? m + 1 : m;
          if (target >= nBars || target === 0) continue;
          if (cl !== curClef) {
            track.clefChanges ??= {};
            track.clefChanges[target] = cl;
            curClef = cl;
          }
        }
      }
      const ms = child(a.el, 'measure-style');
      const mr = childNum(ms, 'multiple-rest');
      if (mr && mr > 1) ctx.multiRest.set(m, Math.round(mr));
    }
    // TAB data lookup.
    const tabLookup = new Map<string, RawNote>();
    if (plan.tabStaff !== undefined && plan.tabStaff !== plan.staff) {
      for (const n of rm.notes) {
        if (n.staff !== plan.tabStaff || !n.pitch || n.rest) continue;
        tabLookup.set(`${Math.round(n.onset)}:${midiOf(n.pitch)}`, n);
      }
    }
    // Group raw notes by voice.
    const staffNotes = rm.notes.filter((n) => n.staff === plan.staff);
    const byVoice = new Map<number, RawNote[]>();
    let dropped = false;
    for (const n of staffNotes) {
      if (!plan.voiceMap.has(n.voice)) {
        dropped = true;
        continue;
      }
      const v = plan.voiceMap.get(n.voice)!;
      const l = byVoice.get(v);
      if (l) l.push(n);
      else byVoice.set(v, [n]);
    }
    if (dropped) warn(`Notes in extra voices of "${track.name}" were dropped`);
    const maxVoice = Math.max(0, ...byVoice.keys());
    const voices: Beat[][] = [];
    for (let v = 0; v <= maxVoice; v++) {
      const items = byVoice.get(v) ?? [];
      const beats = buildVoice(items, v, ts, cap, !!bars[m].pickup || (m === 0 && rm.implicit), track, plan, fretted, tabLookup, st, warn);
      voices.push(beats);
    }
    if (!voices[0] || voices[0].length === 0) voices[0] = barRests(ts);
    // Trim trailing empty voices.
    while (voices.length > 1 && voices[voices.length - 1].length === 0) voices.pop();
    track.bars[m] = { voices };
    for (const v of voices) placedBar.push(placedFor(v));
    ctx.placed.push(placedBar);

    // Track-level events.
    for (const ev of rm.events) {
      const evStaff = ev.staff ?? ev.nextStaff ?? 1;
      if (evStaff !== plan.staff && evStaff !== plan.tabStaff) continue;
      const voice = ev.voice !== undefined ? voiceOf(ev.voice) : ev.nextVoice !== undefined ? voiceOf(ev.nextVoice) : 0;
      collectTrackEvent(ev, m, voice, ev.voice !== undefined, attach, ranges);
    }
  }

  // Resolve attachments.
  const find = (bar: number, voice: number, pos: number, mode: 'at' | 'end'): { beat: Beat; bar: number; voice: number } | undefined => {
    const pb = ctx.placed[bar];
    if (!pb) return undefined;
    const v = pb[voice] && pb[voice].length ? voice : 0;
    const list = pb[v] ?? [];
    if (!list.length) return undefined;
    if (mode === 'at') {
      const hit = list.find((p) => p.onset >= pos - TOL);
      if (hit) return { beat: hit.beat, bar, voice: v };
      if (bar + 1 < ctx.placed.length) {
        const nb = ctx.placed[bar + 1][v]?.[0] ?? ctx.placed[bar + 1][0]?.[0];
        if (nb) return { beat: nb.beat, bar: bar + 1, voice: v };
      }
      return { beat: list[list.length - 1].beat, bar, voice: v };
    }
    let best: Placed | undefined;
    for (const p of list) if (p.onset < pos - TOL) best = p;
    if (best) return { beat: best.beat, bar, voice: v };
    if (bar > 0) {
      const prev = ctx.placed[bar - 1][v] ?? ctx.placed[bar - 1][0];
      const last = prev?.[prev.length - 1];
      if (last) return { beat: last.beat, bar: bar - 1, voice: v };
    }
    return { beat: list[0].beat, bar, voice: v };
  };
  for (const a of attach) {
    if (a.kind === 'wedgeStop') {
      const open = wedges.get(`${a.voice}:${a.number}`) ?? [...wedges.values()].find((w) => w.voice === a.voice) ?? [...wedges.values()][0];
      if (!open) continue;
      const hit = find(a.bar, open.voice, a.pos, 'end');
      open.beat.hairpin = { type: open.type, endBeatId: hit ? hit.beat.id : open.beat.id };
      for (const [k, w] of wedges) if (w === open) wedges.delete(k);
      continue;
    }
    const hit = find(a.bar, a.voice, a.pos, 'at');
    if (!hit) continue;
    const b = hit.beat;
    switch (a.kind) {
      case 'text':
        b.text = { ...(b.text ?? {}) };
        b.text[a.placement] = b.text[a.placement] ? `${b.text[a.placement]} ${a.text}` : a.text;
        break;
      case 'dynamics':
        b.dynamics = a.value;
        break;
      case 'pedal':
        b.pedal = a.value;
        break;
      case 'chord':
        b.chord = a.chord;
        break;
      case 'wedgeStart': {
        b.hairpin = { type: a.type, endBeatId: b.id };
        wedges.set(`${hit.voice}:${a.number}`, { beat: b, type: a.type, bar: hit.bar, voice: hit.voice });
        break;
      }
    }
  }

  // Ranges: let ring, palm mute, octave shifts.
  const open = new Map<string, RangeEv>();
  const closed: Array<{ start: RangeEv; single: boolean; endBar: number; endPos: number }> = [];
  for (const r of ranges) {
    const key = `${r.kind}:${r.number}:${r.voice ?? 'all'}`;
    if (r.start) {
      const prev = open.get(key);
      if (prev) closed.push({ start: prev, single: false, endBar: r.bar, endPos: r.pos });
      open.delete(key);
      if (r.single) closed.push({ start: r, single: true, endBar: r.bar, endPos: r.pos });
      else open.set(key, r);
    } else {
      let s = open.get(key);
      if (s) open.delete(key);
      else {
        for (const [k, v] of open) {
          if (v.kind === r.kind && (r.voice === undefined || v.voice === undefined || v.voice === r.voice)) {
            s = v;
            open.delete(k);
            break;
          }
        }
      }
      if (s) closed.push({ start: s, single: false, endBar: r.bar, endPos: r.pos });
    }
  }
  for (const s of open.values()) closed.push({ start: s, single: false, endBar: nBars, endPos: 0 });
  for (const c of closed) {
    const s = c.start;
    const voices = s.voice !== undefined ? [s.voice] : [0, 1, 2, 3];
    for (const v of voices) {
      let applied = false;
      for (let m = s.bar; m < nBars && !applied && (c.single || m <= c.endBar); m++) {
        let t = 0;
        for (const b of track.bars[m].voices[v] ?? []) {
          const onset = t;
          if (!b.grace) t += durTicks(b.duration);
          if (m === s.bar && onset < s.pos - TOL) continue;
          if (c.single) {
            if (b.grace) continue;
            applyRange(s, b);
            applied = true;
            break;
          }
          if (m === c.endBar && onset >= c.endPos - TOL) break;
          applyRange(s, b);
        }
      }
    }
  }

  // Legato slides: a slide paired with a slur between the same notes.
  const slurStartRefs = new Map<Note, number>();
  const slurEndRefs = new Map<Note, number>();
  for (const p of st.slurPairs) {
    slurStartRefs.set(p.a, (slurStartRefs.get(p.a) ?? 0) + 1);
    slurEndRefs.set(p.b, (slurEndRefs.get(p.b) ?? 0) + 1);
  }
  for (const sp of st.slidePairs) {
    const slur = st.slurPairs.find((p) => p.a === sp.a && p.b === sp.b);
    if (slur) {
      sp.a.slide = 'legato';
      st.slurPairs.splice(st.slurPairs.indexOf(slur), 1);
      const sa = (slurStartRefs.get(sp.a) ?? 1) - 1;
      slurStartRefs.set(sp.a, sa);
      if (sa <= 0) {
        delete sp.a.slurStart;
        delete sp.a.slurSide;
      }
      const sb = (slurEndRefs.get(sp.b) ?? 1) - 1;
      slurEndRefs.set(sp.b, sb);
      if (sb <= 0) delete sp.b.slurEnd;
    } else if (!sp.a.slide) sp.a.slide = 'shift';
  }
  for (const a of st.slideOpen.values()) if (!a.slide) a.slide = 'shift';

  // Tab positions for fretted tracks.
  if (fretted) fingerTrack(track);
  return ctx;
}

function placedFor(beats: Beat[]): Placed[] {
  const out: Placed[] = [];
  let t = 0;
  for (const b of beats) {
    if (b.grace) continue;
    out.push({ onset: t, beat: b });
    t += durTicks(b.duration);
  }
  return out;
}

type AttachEv =
  | { kind: 'text'; bar: number; pos: number; voice: number; placement: 'above' | 'below'; text: string }
  | { kind: 'dynamics'; bar: number; pos: number; voice: number; value: Dynamic }
  | { kind: 'pedal'; bar: number; pos: number; voice: number; value: 'down' | 'up' }
  | { kind: 'chord'; bar: number; pos: number; voice: number; chord: NonNullable<Beat['chord']> }
  | { kind: 'wedgeStart'; bar: number; pos: number; voice: number; type: 'cresc' | 'dim'; number: string }
  | { kind: 'wedgeStop'; bar: number; pos: number; voice: number; number: string };

interface RangeEv {
  kind: 'letRing' | 'palmMute' | 'octave';
  start: boolean;
  /** Applies to the first beat only (a plain "let ring" / "P.M." text without dashes). */
  single?: boolean;
  bar: number;
  pos: number;
  voice?: number;
  number: string;
  shift?: 8 | -8 | 15 | -15;
}

function applyRange(r: RangeEv, b: Beat): void {
  if (r.kind === 'letRing') b.letRing = true;
  else if (r.kind === 'palmMute') b.palmMute = true;
  else if (r.kind === 'octave' && r.shift) {
    if (b.octaveShift) return;
    b.octaveShift = r.shift;
    const oct = (Math.abs(r.shift) === 15 ? 2 : 1) * Math.sign(r.shift);
    for (const n of b.notes) {
      const o = n.pitch.octave - oct;
      if (o >= -1 && o <= 9) n.pitch = { ...n.pitch, octave: o };
    }
  }
}

function collectTrackEvent(ev: RawEvent, bar: number, voice: number, explicitVoice: boolean, attach: AttachEv[], ranges: RangeEv[]): void {
  const el = ev.el;
  const pos = ev.pos;
  if (el.name === 'harmony') {
    const chord = harmonyToChord(el);
    if (chord) attach.push({ kind: 'chord', bar, pos, voice, chord });
    return;
  }
  if (el.name !== 'direction') return;
  const placement: 'above' | 'below' = el.attrs.placement === 'below' ? 'below' : 'above';
  const dtypes = elements(el, 'direction-type');
  const kids = dtypes.flatMap((d) => elements(d));
  const sound = child(el, 'sound');
  const isTempo = kids.some((k) => k.name === 'metronome') || attrNum(sound, 'tempo') !== undefined;
  const isSwing = !!child(sound, 'swing');
  const isScoreLevel =
    isTempo ||
    isSwing ||
    kids.some((k) => k.name === 'rehearsal' || k.name === 'segno' || k.name === 'coda') ||
    !!(sound && (sound.attrs.dacapo || sound.attrs.dalsegno || sound.attrs.fine || sound.attrs.tocoda || sound.attrs.segno || sound.attrs.coda));
  const words = kids.filter((k) => k.name === 'words').map((k) => textOf(k).trim()).filter(Boolean);
  const dashes = kids.find((k) => k.name === 'dashes' || k.name === 'bracket');
  const rangeVoice = explicitVoice ? voice : undefined;
  let handledWords = isScoreLevel;
  const joined = words.join(' ');
  const lr = /let\s*ring/i.test(joined);
  const pm = /^(p\.?\s*m\.?|palm\s*mute)/i.test(joined);
  if (lr || pm) {
    handledWords = true;
    const startDashes = dashes && (dashes.attrs.type ?? 'start') === 'start';
    ranges.push({ kind: lr ? 'letRing' : 'palmMute', start: true, single: !startDashes, bar, pos, voice: rangeVoice ?? voice, number: dashes?.attrs.number ?? '1' });
  } else if (dashes && dashes.attrs.type === 'stop') {
    // A dashes stop closes whichever let ring / P.M. range is open in this voice.
    ranges.push({ kind: 'letRing', start: false, bar, pos, voice: rangeVoice ?? voice, number: dashes.attrs.number ?? '1' });
    ranges.push({ kind: 'palmMute', start: false, bar, pos, voice: rangeVoice ?? voice, number: dashes.attrs.number ?? '1' });
  }
  if (!handledWords) {
    for (const w of words) {
      if (jumpFromWords(w)) continue;
      attach.push({ kind: 'text', bar, pos, voice, placement, text: w });
    }
  }
  for (const k of kids) {
    if (k.name === 'dynamics') {
      const d = elements(k).map((x) => x.name).find((n) => (DYNAMICS as readonly string[]).includes(n));
      if (d) attach.push({ kind: 'dynamics', bar, pos, voice, value: d as Dynamic });
    } else if (k.name === 'wedge') {
      const type = k.attrs.type;
      const number = k.attrs.number ?? '1';
      if (type === 'crescendo' || type === 'diminuendo') attach.push({ kind: 'wedgeStart', bar, pos, voice, type: type === 'crescendo' ? 'cresc' : 'dim', number });
      else if (type === 'stop') attach.push({ kind: 'wedgeStop', bar, pos, voice, number });
    } else if (k.name === 'pedal') {
      const type = k.attrs.type;
      if (type === 'start' || type === 'resume') attach.push({ kind: 'pedal', bar, pos, voice, value: 'down' });
      else if (type === 'stop' || type === 'discontinue') attach.push({ kind: 'pedal', bar, pos, voice, value: 'up' });
    } else if (k.name === 'octave-shift') {
      const type = k.attrs.type;
      const size = attrNum(k, 'size') ?? 8;
      const number = k.attrs.number ?? '1';
      if (type === 'up' || type === 'down') {
        const big = size >= 15;
        const shift = (type === 'down' ? 1 : -1) * (big ? 15 : 8);
        ranges.push({ kind: 'octave', start: true, bar, pos, voice: rangeVoice, number, shift: shift as RangeEv['shift'] });
      } else if (type === 'stop') ranges.push({ kind: 'octave', start: false, bar, pos, voice: rangeVoice, number });
    }
  }
}

function harmonyToChord(el: XmlElement): NonNullable<Beat['chord']> | null {
  const rootEl = child(el, 'root');
  const kindEl = child(el, 'kind');
  const kind = kindEl ? textOf(kindEl).trim() : 'major';
  let rootText = '';
  if (rootEl) {
    const rs = child(rootEl, 'root-step');
    if (rs && rs.attrs.text !== undefined) rootText = rs.attrs.text;
    else rootText = stepAlterText((childText(rootEl, 'root-step') ?? 'C').toUpperCase(), childNum(rootEl, 'root-alter') ?? 0);
  } else {
    const num = child(el, 'numeral') ?? child(el, 'function');
    if (num) rootText = textOf(child(num, 'numeral-root') ?? num).trim();
  }
  let suffix: string;
  if (kindEl && kindEl.attrs.text !== undefined) suffix = kindEl.attrs.text;
  else {
    suffix = KIND_SUFFIX[kind] ?? '';
    if (kind === 'none') rootText = '';
    for (const d of elements(el, 'degree')) {
      const value = childNum(d, 'degree-value');
      const alter = Math.round(childNum(d, 'degree-alter') ?? 0);
      const type = childText(d, 'degree-type') ?? 'add';
      if (value === undefined) continue;
      const acc = alter > 0 ? '#'.repeat(alter) : alter < 0 ? 'b'.repeat(-alter) : '';
      suffix += type === 'subtract' ? `no${value}` : type === 'alter' ? `${acc}${value}` : `add${acc}${value}`;
    }
  }
  let bassText = '';
  const bassEl = child(el, 'bass');
  if (bassEl) {
    const bs = child(bassEl, 'bass-step');
    if (bs && bs.attrs.text !== undefined) bassText = bs.attrs.text;
    else bassText = stepAlterText((childText(bassEl, 'bass-step') ?? 'C').toUpperCase(), childNum(bassEl, 'bass-alter') ?? 0);
  }
  const name = (rootText + suffix + (bassText ? `/${bassText}` : '')).trim();
  if (!name) return null;
  const chord: NonNullable<Beat['chord']> = { name };
  const frame = child(el, 'frame');
  if (frame) {
    const strings = Math.max(1, Math.min(12, Math.round(childNum(frame, 'frame-strings') ?? 6)));
    const frets: (number | null)[] = Array.from({ length: strings }, () => null);
    const fingers: (number | null)[] = Array.from({ length: strings }, () => null);
    let anyFinger = false;
    for (const fn of elements(frame, 'frame-note')) {
      const s = childNum(fn, 'string');
      const f = childNum(fn, 'fret');
      if (s === undefined || f === undefined || s < 1 || s > strings) continue;
      frets[s - 1] = Math.max(0, Math.round(f));
      const fg = childNum(fn, 'fingering');
      if (fg !== undefined) {
        fingers[s - 1] = Math.round(fg);
        anyFinger = true;
      }
    }
    chord.diagram = { frets, baseFret: Math.max(1, Math.round(childNum(frame, 'first-fret') ?? 1)) };
    if (anyFinger) chord.diagram.fingers = fingers;
  }
  return chord;
}

function noteDurations(it: RawNote, cap: number, ts: TimeSignature, pickup: boolean, single: boolean): Duration[] {
  const el = it.el;
  if (it.measureRest || (it.rest && !childText(el, 'type') && single && Math.abs(it.dur - cap) <= TOL)) {
    if (pickup && it.dur > 0 && it.dur < cap - TOL) return durationsForTicks(Math.round(it.dur));
    const tn = baseOfType(childText(el, 'type'));
    if (tn && Number.isInteger(tn) && tn >= 1 && tn <= 64) {
      const d: Duration = { base: tn as DurBase, dots: Math.min(2, elements(el, 'dot').length) as 0 | 1 | 2 };
      if (Math.abs(durTicks(d) - cap) <= TOL) return [d];
    }
    return barRests(ts).map((b) => b.duration);
  }
  let base = baseOfType(childText(el, 'type'));
  const dots = Math.min(2, elements(el, 'dot').length) as 0 | 1 | 2;
  const tm = child(el, 'time-modification');
  let tuplet: Duration['tuplet'];
  if (tm) {
    const actual = Math.round(childNum(tm, 'actual-notes') ?? 0);
    let normal = Math.round(childNum(tm, 'normal-notes') ?? 0);
    const nt = baseOfType(childText(tm, 'normal-type'));
    if (nt && base && nt !== base && normal > 0) normal = Math.round((normal * base) / nt);
    if (actual > 0 && normal > 0 && actual !== normal && actual <= 64 && normal <= 64) tuplet = { actual, normal };
  }
  if (base !== undefined && base > 64) base = 64;
  if (base !== undefined && base >= 1 && Number.isInteger(base)) {
    const d: Duration = tuplet ? { base: base as DurBase, dots, tuplet } : { base: base as DurBase, dots };
    return [d];
  }
  if (it.grace) return [{ base: 8, dots: 0 }];
  if (it.dur > 0) return durationsForTicks(Math.round(it.dur));
  return [{ base: 4, dots: 0 }];
}

function buildVoice(
  items: RawNote[],
  v: number,
  ts: TimeSignature,
  cap: number,
  pickup: boolean,
  track: Track,
  plan: TrackPlan,
  fretted: boolean,
  tabLookup: Map<string, RawNote>,
  st: NotationState,
  warn: (s: string) => void,
): Beat[] {
  const beats: Beat[] = [];
  let expected = 0;
  let prev: Beat[] | null = null;
  let prevItem: RawNote | null = null;
  const pushRests = (gap: number) => {
    for (const d of durationsForTicks(Math.round(gap))) {
      const r = createRest(d);
      if (v > 0) r.hidden = true;
      beats.push(r);
      expected += durTicks(d);
    }
  };
  for (const it of items) {
    if (beats.length >= LIMITS.beatsPerVoice) {
      warn(`More than ${LIMITS.beatsPerVoice} beats in a bar; the rest were dropped`);
      break;
    }
    if (it.chord && prev && prevItem && !it.rest && !prevItem.rest) {
      if (prev[0].notes.length >= LIMITS.notesPerBeat) {
        warn(`Chord with more than ${LIMITS.notesPerBeat} notes truncated`);
        continue;
      }
      addNote(prev, it, track, plan, fretted, tabLookup, st, warn);
      prevItem = it;
      continue;
    }
    if (it.grace) {
      const d = noteDurations(it, cap, ts, pickup, false)[0];
      const b: Beat = { id: newId(), duration: d, rest: it.rest, notes: [], grace: it.grace };
      beats.push(b);
      if (!it.rest) addNote([b], it, track, plan, fretted, tabLookup, st, warn);
      if (b.notes.length === 0) b.rest = true;
      applyBeatProps(it.el, b);
      prev = [b];
      prevItem = it;
      continue;
    }
    const gap = it.onset - expected;
    if (gap > TOL) pushRests(gap);
    const durs = noteDurations(it, cap, ts, pickup, items.length === 1);
    const pieces: Beat[] = durs.map((d) => ({ id: newId(), duration: { ...d }, rest: it.rest || !it.pitch, notes: [] }));
    if (!it.rest && it.pitch) addNote(pieces, it, track, plan, fretted, tabLookup, st, warn);
    for (const p of pieces) {
      if (p.notes.length === 0) p.rest = true;
      applyBeatProps(it.el, p);
      beats.push(p);
      expected += durTicks(p.duration);
    }
    prev = pieces;
    prevItem = it;
  }
  // Pad voice 0 to the bar length (not in pickups).
  if (v === 0 && !pickup && expected < cap - TOL && beats.length > 0) pushRests(cap - expected);
  for (const b of beats) {
    if (b.notes.length && b.notes.every((n) => n.hidden)) {
      b.hidden = true;
      for (const n of b.notes) delete n.hidden;
    }
  }
  return beats;
}

/** Beat-level properties carried by a note element (rests included). */
function applyBeatProps(el: XmlElement, b: Beat): void {
  if (el.attrs['print-object'] === 'no' && b.rest) b.hidden = true;
  const typeEl = child(el, 'type');
  if (typeEl?.attrs.size === 'cue' || child(el, 'cue')) b.small = true;
  if (b.rest && el.attrs.color && /^#[0-9a-fA-F]{6}/.test(el.attrs.color)) b.color = el.attrs.color.slice(0, 7);
}

function addNote(
  pieces: Beat[],
  it: RawNote,
  track: Track,
  plan: TrackPlan,
  fretted: boolean,
  tabLookup: Map<string, RawNote>,
  st: NotationState,
  warn: (s: string) => void,
): void {
  const xp = it.pitch;
  let written: Pitch | undefined;
  if (xp) {
    const shiftOct = plan.xmlShift / 12;
    if (Number.isInteger(shiftOct)) written = { ...xp, octave: xp.octave - shiftOct };
    else written = spellMidi(midiOf(xp) - plan.xmlShift, 0);
  } else if (plan.tabOnly && it.string !== undefined && it.fret !== undefined && track.tuning) {
    const s = track.tuning[it.string - 1];
    if (s !== undefined) written = spellMidi(s + (track.capo ?? 0) + it.fret - track.writtenToSounding, 0);
  }
  if (!written) return;
  if (written.octave < -1 || written.octave > 9) {
    warn(`Note ${written.step}${written.octave} is outside the supported range and was skipped`);
    return;
  }
  let string = it.string;
  let fret = it.fret;
  if (fretted && (string === undefined || fret === undefined) && xp) {
    const t = tabLookup.get(`${Math.round(it.onset)}:${midiOf(xp)}`);
    if (t) {
      string ??= t.string;
      fret ??= t.fret;
    }
  }
  const tieStart =
    elements(it.el, 'tie').some((t) => t.attrs.type === 'start') ||
    elements(it.el, 'notations').some((n) => elements(n, 'tied').some((t) => t.attrs.type === 'start'));
  pieces.forEach((beat, i) => {
    const note: Note = { id: newId(), pitch: { ...written! } };
    if (fretted && string !== undefined && string >= 1 && string <= (track.tuning?.length ?? 0)) {
      note.string = Math.round(string);
      if (fret !== undefined && fret >= 0) note.fret = Math.round(fret);
    }
    if (i === 0) applyNoteNotations(it.el, note, beat, st, fretted);
    if (pieces.length > 1) {
      // A note split into tied pieces: inner ties always, the last piece keeps the element's own tie.
      if (i < pieces.length - 1) note.tieToNext = true;
      else if (tieStart) note.tieToNext = true;
      else delete note.tieToNext;
    }
    if (beat.notes.length < LIMITS.notesPerBeat) beat.notes.push(note);
    beat.rest = false;
  });
}

const ARTIC_MAP: Record<string, Articulation> = {
  staccato: 'staccato',
  staccatissimo: 'staccatissimo',
  spiccato: 'staccatissimo',
  accent: 'accent',
  'strong-accent': 'marcato',
  tenuto: 'tenuto',
};

function addArt(b: Beat, a: Articulation): void {
  const l = b.articulations ?? [];
  if (!l.includes(a)) l.push(a);
  b.articulations = l;
}

function applyNoteNotations(el: XmlElement, note: Note, beat: Beat, st: NotationState, fretted: boolean): void {
  const voiceKey = childText(el, 'voice') || '1';
  if (elements(el, 'tie').some((t) => t.attrs.type === 'start')) note.tieToNext = true;
  if (el.attrs['print-object'] === 'no') note.hidden = true;
  if (el.attrs.color && /^#[0-9a-fA-F]{6}/.test(el.attrs.color)) note.color = el.attrs.color.slice(0, 7).toUpperCase();
  const stem = childText(el, 'stem');
  if (stem === 'up' || stem === 'down') beat.stem = stem;
  else if (stem === 'none') beat.stem = 'hidden';
  const acc = child(el, 'accidental');
  if (acc && (acc.attrs.cautionary === 'yes' || acc.attrs.editorial === 'yes' || acc.attrs.parentheses === 'yes' || acc.attrs.bracket === 'yes')) note.accidentalDisplay = 'cautionary';
  const nh = child(el, 'notehead');
  if (nh) {
    const t = textOf(nh).trim();
    if (t === 'x' || t === 'cross') {
      if (fretted) note.dead = true;
      else note.head = 'x';
    } else if (t === 'diamond') note.head = 'diamond';
    else if (t === 'slash') note.head = 'slash';
    else if (t === 'circle-x') note.head = 'circleX';
    if (nh.attrs.parentheses === 'yes') note.ghost = true;
  }
  for (const play of elements(el, 'play')) {
    if (elements(play, 'mute').some((m) => /palm/i.test(textOf(m)))) beat.palmMute = true;
  }
  // Lyrics.
  for (const ly of elements(el, 'lyric')) {
    const texts = elements(ly, 'text').map((t) => textOf(t));
    if (!texts.length) continue;
    let text = texts.join('‿');
    const syl = childText(ly, 'syllabic');
    if (syl === 'begin' || syl === 'middle') text += '-';
    const num = ly.attrs.number ?? ly.attrs.name ?? '1';
    const n = Number(num);
    let idx: number;
    if (Number.isInteger(n) && n >= 1 && n <= 16) idx = n - 1;
    else {
      if (!st.lyricVerses.includes(num)) st.lyricVerses.push(num);
      idx = Math.min(15, st.lyricVerses.indexOf(num));
    }
    const l = [...(beat.lyrics ?? [])];
    while (l.length <= idx) l.push('');
    l[idx] = text;
    beat.lyrics = l;
  }
  const bends: XmlElement[] = [];
  let trill = false;
  let wavy = false;
  for (const nt of elements(el, 'notations')) {
    for (const k of elements(nt)) {
      switch (k.name) {
        case 'tied':
          if (k.attrs.type === 'start') note.tieToNext = true;
          else if (k.attrs.type === 'let-ring') beat.letRing = true;
          break;
        case 'slur': {
          const num = `${voiceKey}:${k.attrs.number ?? '1'}`;
          if (k.attrs.type === 'start') {
            note.slurStart = true;
            if (k.attrs.placement === 'above' || k.attrs.placement === 'below') note.slurSide = k.attrs.placement;
            st.slurOpen.set(num, note);
          } else if (k.attrs.type === 'stop') {
            note.slurEnd = true;
            const a = st.slurOpen.get(num);
            if (a) {
              st.slurPairs.push({ a, b: note });
              st.slurOpen.delete(num);
            }
          }
          break;
        }
        case 'slide':
        case 'glissando': {
          const num = `${voiceKey}:${k.attrs.number ?? '1'}`;
          if (k.attrs.type === 'start') st.slideOpen.set(num, note);
          else if (k.attrs.type === 'stop') {
            const a = st.slideOpen.get(num);
            if (a) {
              st.slidePairs.push({ a, b: note });
              st.slideOpen.delete(num);
            }
          }
          break;
        }
        case 'ornaments':
          for (const o of elements(k)) {
            if (o.name === 'trill-mark') {
              beat.ornament = 'trill';
              trill = true;
            } else if (o.name === 'mordent' || o.name === 'inverted-mordent') beat.ornament = 'mordent';
            else if (o.name === 'turn' || o.name === 'inverted-turn' || o.name === 'delayed-turn' || o.name === 'delayed-inverted-turn') beat.ornament = 'turn';
            else if (o.name === 'wavy-line') {
              wavy = true;
              const num = Number(o.attrs.number ?? '1');
              const set = st.vibOpen.get(num) ?? new Set<string>();
              if (o.attrs.type === 'start') set.add(voiceKey);
              else if (o.attrs.type === 'stop') set.delete(voiceKey);
              st.vibOpen.set(num, set);
            } else if (o.name === 'tremolo' && (o.attrs.type ?? 'single') === 'single') {
              const n = Math.round(Number(textOf(o).trim() || '3'));
              beat.tremolo = Math.max(1, Math.min(3, n)) as 1 | 2 | 3;
            }
          }
          break;
        case 'technical':
          for (const t of elements(k)) {
            switch (t.name) {
              case 'string':
                if (t.attrs.placement !== undefined || !child(k, 'fret')) note.stringCircle = true;
                break;
              case 'hammer-on':
              case 'pull-off':
                if (t.attrs.type === 'start') note.hammerPull = true;
                break;
              case 'bend':
                bends.push(t);
                break;
              case 'harmonic':
                note.harmonic = child(t, 'artificial') ? 'artificial' : 'natural';
                break;
              case 'fingering': {
                const f = textOf(t).trim();
                if (f) note.fingering = f;
                break;
              }
              case 'pluck': {
                const f = textOf(t).trim();
                if (f) note.rhFingering = f;
                break;
              }
              case 'tap':
                if (t.attrs.hand === 'left') note.leftHandTap = true;
                else if (t.attrs.hand === 'right') beat.tap = true;
                else note.tap = true;
                break;
              case 'other-technical': {
                const x = textOf(t).trim().toLowerCase();
                if (/^(p\.?\s*m\.?|palm\s*mute)/.test(x)) beat.palmMute = true;
                else if (x === 'slap' || x === 's') beat.slap = true;
                else if (x === 'pop' || x === 'p') beat.pop = true;
                break;
              }
              case 'up-bow':
                beat.pick = 'up';
                break;
              case 'down-bow':
                beat.pick = 'down';
                break;
              case 'open':
                beat.mute = 'open';
                break;
              case 'stopped':
                beat.mute = 'closed';
                break;
              case 'half-muted':
                beat.mute = 'halfOpen1';
                break;
              case 'harmon-mute':
                beat.mute = 'halfOpen2';
                break;
            }
          }
          break;
        case 'articulations':
          for (const a of elements(k)) {
            const mapped = ARTIC_MAP[a.name];
            if (mapped) addArt(beat, mapped);
            else if (a.name === 'detached-legato') {
              addArt(beat, 'tenuto');
              addArt(beat, 'staccato');
            } else if (a.name === 'breath-mark') beat.breath = true;
            else if (a.name === 'scoop' || a.name === 'plop' || a.name === 'doit' || a.name === 'falloff') {
              if (a.attrs['line-shape'] === 'straight') {
                note.slide = a.name === 'scoop' ? 'inBelow' : a.name === 'plop' ? 'inAbove' : a.name === 'doit' ? 'outUp' : 'outDown';
              } else beat.jazz = a.name === 'falloff' ? 'fall' : a.name;
            }
          }
          break;
        case 'fermata':
          addArt(beat, 'fermata');
          break;
        case 'arpeggiate':
          beat.arpeggio = k.attrs.direction === 'down' ? 'down' : 'up';
          break;
        case 'dynamics': {
          const d = elements(k).map((x) => x.name).find((n) => (DYNAMICS as readonly string[]).includes(n));
          if (d) beat.dynamics = d as Dynamic;
          break;
        }
      }
    }
  }
  if (!trill) {
    const open = [...st.vibOpen.values()].some((s) => s.has(voiceKey));
    if (wavy || open) note.vibrato = true;
  } else {
    for (const s of st.vibOpen.values()) s.delete(voiceKey);
  }
  if (bends.length) {
    const pts = bendPoints(bends);
    if (pts.length > 1) note.bend = { points: pts };
  }
}

function bendPoints(bends: XmlElement[]): BendPoint[] {
  const pts: BendPoint[] = [];
  let cur = 0;
  const changes = bends.filter((b) => !child(b, 'pre-bend'));
  const n = changes.length;
  let k = 0;
  for (const b of bends) {
    const alter = childNum(b, 'bend-alter') ?? 0;
    if (child(b, 'pre-bend')) {
      cur = Math.abs(alter);
      pts.length = 0;
      pts.push({ t: 0, semis: cur });
      continue;
    }
    const delta = child(b, 'release') ? -Math.abs(alter) : alter;
    const fb = attrNum(b, 'first-beat');
    const lb = attrNum(b, 'last-beat');
    let f = fb !== undefined ? fb / 100 : k / n;
    let l = lb !== undefined ? lb / 100 : (k + 0.5) / n;
    k++;
    if (!pts.length) pts.push({ t: 0, semis: cur });
    const lastT = pts[pts.length - 1].t;
    f = Math.max(lastT, Math.min(1, f));
    l = Math.max(f, Math.min(1, l));
    if (f > lastT + 1e-9) pts.push({ t: f, semis: cur });
    cur = Math.round((cur + delta) * 100) / 100;
    if (l > pts[pts.length - 1].t + 1e-9 || pts.length === 1) pts.push({ t: l, semis: cur });
    else pts[pts.length - 1] = { t: l, semis: cur };
  }
  if (pts.length && pts[pts.length - 1].t < 1 - 1e-9) pts.push({ t: 1, semis: cur });
  if (pts.length === 1) pts.push({ t: 1, semis: cur });
  return pts.map((p) => ({ t: Math.round(p.t * 1000) / 1000, semis: p.semis }));
}

/** Merge runs of empty measures marked as a multi-measure rest into one bar with `multiRest`. */
function collapseMultiRests(score: Score, ctxs: TrackCtx[]): void {
  const starts = new Map<number, number>();
  for (const c of ctxs) for (const [m, n] of c.multiRest) starts.set(m, Math.max(starts.get(m) ?? 0, n));
  if (!starts.size) return;
  const isEmptyBar = (m: number) =>
    score.tracks.every((t) => t.bars[m].voices.every((v, vi) => (vi === 0 ? v.every((b) => b.rest) : v.length === 0 || v.every((b) => b.rest))));
  const remove: number[] = [];
  for (const [m, n] of [...starts].sort((a, b) => a[0] - b[0])) {
    const end = m + n;
    if (end > score.bars.length) continue;
    let ok = true;
    for (let k = m; k < end && ok; k++) {
      if (!isEmptyBar(k)) ok = false;
      if (k > m) {
        const b = score.bars[k];
        if (b.timeSig || b.keySig || b.repeatStart || b.repeatEnd || b.endings || b.direction || b.section) ok = false;
        if (score.tempos.some((t) => t.bar === k)) ok = false;
      }
    }
    if (!ok) continue;
    score.bars[m].multiRest = n;
    // Keep the last bar's right-side properties.
    const last = score.bars[end - 1];
    if (last.barline) score.bars[m].barline = last.barline;
    for (let k = m + 1; k < end; k++) remove.push(k);
  }
  if (!remove.length) return;
  const rm = new Set(remove);
  const map: number[] = [];
  let j = 0;
  for (let i = 0; i < score.bars.length; i++) {
    map.push(j);
    if (!rm.has(i)) j++;
  }
  score.bars = score.bars.filter((_, i) => !rm.has(i));
  for (const t of score.tracks) {
    t.bars = t.bars.filter((_, i) => !rm.has(i));
    if (t.clefChanges) {
      const cc: Record<number, Clef> = {};
      for (const [k, v] of Object.entries(t.clefChanges)) if (!rm.has(Number(k))) cc[map[Number(k)]] = v;
      t.clefChanges = cc;
    }
  }
  for (const tm of score.tempos) tm.bar = map[tm.bar] ?? tm.bar;
}
